/**
 * Which agency data feeds this client: attach an ad account or a branch to
 * the client, or detach it. Sources that need no attachment (reviews of this
 * brand, the funnel) are listed as always on.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { reportsApi, type Attachment, type Catalogue, type Client } from './reports-api.ts';

export function SourcesPanel({
  organisationId,
  client,
  catalogue,
}: {
  organisationId: string;
  client: Client;
  catalogue: Catalogue;
}): ReactNode {
  const [attached, setAttached] = useState<Attachment[] | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [options, setOptions] = useState<{ id: string; label: string }[]>([]);
  const [choice, setChoice] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const load = () =>
    reportsApi
      .sources(organisationId, client.id)
      .then(setAttached)
      .catch(() => setAttached([]));
  useEffect(() => {
    setAttached(null);
    void load();
  }, [organisationId, client.id]);

  const startAdding = async (source: string) => {
    setAdding(source);
    setProblem(null);
    try {
      const items = await reportsApi.attachable(organisationId, source);
      setOptions(items);
      setChoice(items[0]?.id ?? '');
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Could not list accounts.');
    }
  };

  return (
    <section className="card rh-sources" aria-label="Connected data">
      <h2>Connected data</h2>
      <p className="panel-note">
        Dashboards and reports for {client.name} read only the accounts attached here.
      </p>
      <ul className="rh-source-list">
        {catalogue.sources.map((source) => {
          const mine = (attached ?? []).filter((item) => item.source === source.key);
          return (
            <li key={source.key}>
              <div className="rh-source-head">
                <strong>{source.label}</strong>
                {source.needs_attachment ? (
                  <button
                    type="button"
                    className="linklike"
                    onClick={() => startAdding(source.key)}
                  >
                    + Attach
                  </button>
                ) : (
                  <span className="rv-tag">Always on</span>
                )}
              </div>
              {source.needs_attachment &&
                (mine.length === 0 ? (
                  <small className="rh-source-none">
                    Nothing attached: widgets show “attach an account”.
                  </small>
                ) : (
                  <ul>
                    {mine.map((item) => (
                      <li key={item.id} className="rh-attached">
                        <span>{item.label ?? item.source_ref}</span>
                        <button
                          type="button"
                          className="linklike"
                          aria-label={`Detach ${item.label ?? 'account'}`}
                          onClick={async () => {
                            await reportsApi.detach(organisationId, client.id, item.id);
                            void load();
                          }}
                        >
                          Detach
                        </button>
                      </li>
                    ))}
                  </ul>
                ))}
              {adding === source.key && (
                <div className="rh-attach">
                  {options.length === 0 ? (
                    <small>
                      No {source.label} accounts are connected yet.{' '}
                      <a href="#/data">Connect one in Data → Integrations</a>, then attach it here.
                    </small>
                  ) : (
                    <>
                      <select
                        aria-label={`${source.label} account`}
                        value={choice}
                        onChange={(event) => setChoice(event.target.value)}
                      >
                        {options.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn small"
                        disabled={!choice}
                        onClick={async () => {
                          try {
                            await reportsApi.attach(organisationId, client.id, source.key, choice);
                            setAdding(null);
                            void load();
                          } catch (error) {
                            setProblem(
                              error instanceof Error ? error.message : 'Could not attach.',
                            );
                          }
                        }}
                      >
                        Attach
                      </button>
                    </>
                  )}
                  <button type="button" className="linklike" onClick={() => setAdding(null)}>
                    Cancel
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {problem && (
        <p className="rb-banner" role="alert">
          {problem}
        </p>
      )}
    </section>
  );
}
